import { ElementRef } from "@angular/core";
import {
  AbstractControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";

import { TPagination } from "@custom-types/sorting";

export class Utilities {
  static downloadAttachment = (file: Blob, fileName: string): void => {
    const fileURL = URL.createObjectURL(file);
    const fileLink = document.createElement("a");
    fileLink.href = fileURL;
    fileLink.download = `${fileName}`;
    document.body.appendChild(fileLink);
    fileLink.click();
    URL.revokeObjectURL(fileURL);
    fileLink.remove();
  };
  /**
   * @info checkForExceptionsNumberType method is used for checking if the number is valid and doesn't contain special characters
   * Example:
   * yourObject = { initials: 1,234 or 1234 }
   * Expected outcome: true
   */
  static checkForExceptionsNumberType = (input: string): boolean => {
    const notAllowedKeys = [
      "~",
      "!",
      "#",
      "@",
      "$",
      "%",
      "^",
      "&",
      "*",
      "?",
      "(",
      ")",
      "_",
      "+",
      "-",
      "=",
      "[",
      "]",
      "{",
      "}",
      ";",
      ":",
      "|",
      "<",
      ">",
      "e",
      "E",
    ];

    // Check if any invalid characters are present
    const containsSpecialChar = notAllowedKeys.some((substring) =>
      input.includes(substring)
    );

    // Allow only one comma, validate positive number after removing comma
    const commaCount = (input.match(/,/g) || []).length;

    const sanitizedValue = input.replace(/\./g, "").replace(",", ".");

    return (
      !containsSpecialChar &&
      commaCount <= 1 &&
      !isNaN(Number(sanitizedValue)) &&
      Number(sanitizedValue) >= 0
    );
  };

  static stringIsInvalid(value: string): boolean {
    return !value || value.trim().length === 0;
  }

  /**
   * @info formatInputValue method is used for formatting the input values of the name initials.
   * Example:
   * yourObject = { initials: a b }
   * Expected outcome: A. B.
   * */
  static formatInputValue(formGroup: FormGroup, controlName: string): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      control?.setErrors({ required: true });
      return;
    }
    const validCharacters = currentValue.match(/[a-zA-Z]/g);
    if (!validCharacters || validCharacters.length === 0) {
      control?.setErrors({ incorrect: true });
    } else {
      control?.setErrors(null);
      const formattedValue = this.transformInitials(validCharacters);
      control?.setValue(formattedValue);
    }
  }

  /**
   * @info formatLastNameValue method is used for capitalizing the first letter of the lastName field.
   * Example:
   * yourObject = { lastName: talero }
   * Expected outcome: Talero
   * */
  static formatLastNameValue(formGroup: FormGroup, controlName: string): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      control?.setErrors({ required: true });
      return;
    }
    this.setFormattedValue(control, this.transformFirstLetter, currentValue);
  }

  /**
   * @info formatPhoneNumberValue method is used for formatting phone number values.
   * Example:
   * yourObject = { phoneNumber: 0317891231 }
   * Expected outcome: +0317891231
   * */
  static formatPhoneNumberValue(
    formGroup: FormGroup,
    controlName: string
  ): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      control?.setErrors({ required: true });
      return;
    }
    this.setFormattedValue(control, this.addPlusToPhoneNumber, currentValue);
  }

  /**
   * @info formatPercentageValue method is used for formatting amount values.
   * Example:
   * yourObject = { amountPercentage: 123 }
   * Expected outcome: 123%
   * */
  static formatPercentageValue(
    formGroup: FormGroup,
    controlName: string,
    addPercentage: boolean
  ): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      return;
    }
    if (addPercentage) {
      this.setFormattedValue(control, this.addPercentageToAmount, currentValue);
    } else {
      this.setFormattedValue(
        control,
        this.removePercentageFromAmount,
        currentValue
      );
    }
  }

  static removePointerEvents(elementRef: ElementRef): void {
    const elements = elementRef.nativeElement.querySelectorAll(
      "label, mat-datepicker, mat-datepicker-toggle, div.mat-mdc-form-field-icon-suffix"
    );
    elements.forEach((element: HTMLElement): void => {
      element.style.pointerEvents = "none";
    });
  }
  static convertPercentageToNumber(value: string): number {
    if (value && value.slice(-1) === "%") {
      value = value.slice(0, -1);
    }
    return parseFloat(value);
  }

  static checkFormFieldIfEmpty(
    formGroup: FormGroup,
    controlName: string
  ): boolean {
    const control = formGroup.get(controlName);
    return this.stringIsInvalid(control?.value) || control?.value === null;
  }

  static getEnumKeys(enumObject: any): Array<string> {
    return Object.keys(enumObject).filter((key: string): boolean =>
      isNaN(Number(key))
    );
  }

  static numberValidator(percentage?: boolean): ValidatorFn {
    const pattern = percentage ? /^\d+(\.\d+)?%?$/ : /^\d+(\.\d+)?$/;
    return Validators.pattern(pattern);
  }

  static transformFirstLetter(value: string): string {
    if (value[0] !== value[0].toUpperCase()) {
      return value[0].toUpperCase() + value.slice(1);
    }

    return value;
  }

  static addPercentageToAmount(value: string): string {
    if (value && value.slice(-1) !== "%") {
      return value + "%";
    }
    return value;
  }

  static removePercentageFromAmount(value: string): string {
    if (value && value.slice(-1) === "%") {
      return value.replace("%", "");
    }
    return value;
  }

  static addPlusToPhoneNumber(value: string): string {
    if (value[0] !== "+") {
      return "+" + value;
    }

    return value;
  }

  static transformInitials(characters: Array<string>): string {
    return characters.join(".").toUpperCase() + ".";
  }

  static setFormattedValue(
    control: AbstractControl<any, any> | null,
    stringTransformer: (value: string) => void,
    value: string
  ): void {
    control?.setErrors(null);
    const formattedValue = stringTransformer(value);
    control?.setValue(formattedValue);
  }

  static getUniqueItemsForArray<T, K extends keyof T>(array: T[], key: K): T[] {
    return array.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t[key] === item[key])
    );
  }

  static formatDescriptionValue(
    costControl: AbstractControl,
    controlName: string
  ): void {
    const costFormGroup = costControl as FormGroup;
    switch (controlName) {
      case "descriptionFixedAmount":
        if (!costFormGroup.get("amount")?.value) {
          costFormGroup.get(controlName)?.setValue(null);
        }
        break;
      case "descriptionPercentage":
        if (!costFormGroup.get("percentage")?.value) {
          costFormGroup.get(controlName)?.setValue(null);
        }
        break;
    }
  }

  static getPageIndex(pageIndex: number | undefined): number {
    return pageIndex ? pageIndex - 1 : 0;
  }

  static getPaginationTotalRecords(
    pagination: Partial<TPagination> | undefined
  ): number {
    return pagination?.totalRecords ?? 0;
  }
}
